import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, Input, FormField, Alert, Spinner, Heading } from "@/components/ds";
import styles from "./LoginForm.module.css";

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error?.message ?? "Invalid email or password");
        return;
      }

      const redirectTo = redirectParam && redirectParam.startsWith("/")
        ? redirectParam
        : data.data.redirectTo;
      navigate(redirectTo);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <Heading level={1} size="h3">Log in</Heading>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormField label="Email" htmlFor="email">
        <Input
          type="email"
          name="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />
      </FormField>

      <FormField label="Password" htmlFor="password">
        <Input
          type="password"
          name="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
      </FormField>

      <Button type="submit" disabled={loading} fullWidth>
        {loading ? <Spinner size="sm" /> : "Log in"}
      </Button>

      <p className={styles.footer}>
        <Link to="/forgot-password" className={styles.link}>
          Forgot your password?
        </Link>
      </p>

      <p className={styles.footer}>
        Don&apos;t have an account?{" "}
        <a href="/signup" className={styles.link}>
          Create an account
        </a>
      </p>
    </form>
  );
}
